/**
 * employeeGanttRowBuilder.ts
 * Pure builder: tier → employee → assignment-bar treegrid for the Employee
 * Resource Timeline (UX overhaul), reusing the Highcharts GanttPoint/Series
 * shape (and the inclusive→exclusive end-date +1-day convention) from
 * masterGanttRowBuilder so ScheduleGantt renders it unchanged.
 */

import type { GanttSeries, GanttPoint } from './masterGanttRowBuilder';
import type { TierGroup } from './employeeScheduleShared';
import { assignmentPrimaryLabel } from './employeeScheduleShared';
import type { EmployeeBar } from '../../services/scheduleEmployeeBars';

const DAY_MS = 24 * 60 * 60 * 1000;
function toMs(iso: string): number {
  return Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));
}

export function buildEmployeeGanttData(
  groups: TierGroup[],
  barsByUser: Map<string, EmployeeBar[]>,
): GanttSeries[] {
  const data: GanttPoint[] = [];
  for (const g of groups) {
    data.push({ id: `tier-${g.label}`, name: g.label, taskType: 'project', status: null, eventType: null, collapsed: false });
    for (const u of g.users) {
      data.push({ id: `emp-${u.userKey}`, parent: `tier-${g.label}`, name: u.displayName, taskType: 'workOrder', status: null, eventType: null });
      for (const b of barsByUser.get(u.userKey) ?? []) {
        data.push({
          id: `bar-${b.assignmentKey}`,
          parent: `emp-${u.userKey}`,
          // Wave 3 (percent retirement): the "· N%" suffix is dropped — the
          // bar's span IS the busy days (presence, not a fraction).
          name: assignmentPrimaryLabel(b),
          start: toMs(b.startDate),
          end: toMs(b.endDate) + DAY_MS,
          taskType: 'employeeAssignment',
          status: b.status,
          eventType: null,
          custom: { assignmentKey: b.assignmentKey, conflictType: b.conflictType },
        });
      }
    }
  }
  return [{ name: 'Employees', data }];
}

/** Total treegrid rows (for sizing the chart height). */
export function employeeGanttRowCount(groups: TierGroup[], barsByUser: Map<string, EmployeeBar[]>): number {
  let n = 0;
  for (const g of groups) {
    n += 1 + g.users.length;
    for (const u of g.users) n += barsByUser.get(u.userKey)?.length ?? 0;
  }
  return n;
}
